import type { CustomFieldFilter, SavedFilterViewFilters } from "./types";

export interface HandledOsDefaultView {
  systemDefaultKey: string;
  name: string;
  filters: SavedFilterViewFilters;
  sortOrder: number;
}

// Default table columns when no view overrides them: identity, owner, and the
// four enrichment scores (Account Brief + long-form detail fields stay hidden,
// surfaced instead by the dedicated score views below).
export const HANDLED_OS_ACCOUNT_VISIBLE_COLUMNS = [
  "name",
  "owner",
  "cf:Account_Analysis_Score__c",
  "cf:AI_Readiness_Score__c",
  "cf:Financial_Pressure_Score__c",
  "cf:Onboarding_Strain_Score__c",
  "lastInteraction",
] as const;

export const HANDLED_OS_OPPORTUNITY_VISIBLE_COLUMNS = [
  "name",
  "account",
  "owner",
  "amount",
  "closeDate",
  "stage",
] as const;

const HANDLED_OS_ACCOUNT_SUPPORTED_EXTRA_FILTERS = [
  "search",
  "owners",
  "myAccounts",
  "receivedSignals",
  "customFieldFilters",
  "visibleColumns",
] as const;

const HANDLED_OS_OPPORTUNITY_SUPPORTED_EXTRA_FILTERS = [
  "search",
  "owners",
  "stages",
  "amountMin",
  "amountMax",
  "closeDateBucket",
  "isClosed",
  "isWon",
  "visibleColumns",
] as const;

// Handled OS is sales-opportunity focused (NOT risk-signal focused like Mercury).
// The enrichment pipeline only populates scores / brief / detail on accounts it
// has enriched (Is_Enriched__c = true), so the score views filter to those.
const ENRICHED_TRUE: CustomFieldFilter = {
  field: "Is_Enriched__c",
  operator: "eq",
  value: "true",
};

// Handled OS emits sales-opportunity signals (not risk signals). The list_accounts
// RPC applies received_signal_types with `?|` (OR), so listing these matches
// accounts that have received ANY of them.
const HANDLED_OS_SIGNAL_TYPES = [
  "new_hire_detected",
  "job_opening_detected",
  "news_published",
] as const;

function accountFilters(filters: Partial<SavedFilterViewFilters> = {}): SavedFilterViewFilters {
  return {
    supportedExtraFilters: [...HANDLED_OS_ACCOUNT_SUPPORTED_EXTRA_FILTERS],
    sortColumn: "last_signal_at",
    sortDirection: "desc",
    visibleColumns: [...HANDLED_OS_ACCOUNT_VISIBLE_COLUMNS],
    ...filters,
  };
}

function opportunityFilters(filters: Partial<SavedFilterViewFilters> = {}): SavedFilterViewFilters {
  return {
    supportedExtraFilters: [...HANDLED_OS_OPPORTUNITY_SUPPORTED_EXTRA_FILTERS],
    sortColumn: "close_date",
    sortDirection: "asc",
    visibleColumns: [...HANDLED_OS_OPPORTUNITY_VISIBLE_COLUMNS],
    ...filters,
  };
}

/**
 * A "score leaderboard" view: only enriched accounts, sorted high→low by the
 * given score column, showing identity + that theme's score and detail.
 */
function scoreView(scoreColumn: string, detailColumn: string): Partial<SavedFilterViewFilters> {
  return {
    customFieldFilters: [ENRICHED_TRUE],
    sortColumn: scoreColumn,
    sortDirection: "desc",
    visibleColumns: ["name", "owner", scoreColumn, detailColumn],
  };
}

export const HANDLED_OS_ACCOUNT_VIEWS: HandledOsDefaultView[] = [
  // Default: my own enriched accounts (owner = signed-in user, resolved server-side
  // via user_identity_link). myAccounts:true renders the "My accounts" tab so the
  // scoping is visible in the toolbar. Sorted by overall Account Analysis score.
  {
    systemDefaultKey: "accounts.handled_os_my_accounts",
    name: "My Accounts",
    filters: accountFilters({
      myAccounts: true,
      customFieldFilters: [ENRICHED_TRUE],
      sortColumn: "cf:Account_Analysis_Score__c",
      sortDirection: "desc",
      visibleColumns: [...HANDLED_OS_ACCOUNT_VISIBLE_COLUMNS],
    }),
    sortOrder: -60,
  },
  {
    systemDefaultKey: "accounts.handled_os_account_intel",
    name: "Account Intel",
    filters: accountFilters(scoreView("cf:Account_Analysis_Score__c", "cf:Account_Brief__c")),
    sortOrder: -50,
  },
  {
    systemDefaultKey: "accounts.handled_os_ai_readiness",
    name: "AI Readiness",
    filters: accountFilters(scoreView("cf:AI_Readiness_Score__c", "cf:AI_Readiness_Detail__c")),
    sortOrder: -40,
  },
  {
    systemDefaultKey: "accounts.handled_os_financial_pressure",
    name: "Financial Pressure",
    filters: accountFilters(scoreView("cf:Financial_Pressure_Score__c", "cf:Financial_Pressure_Detail__c")),
    sortOrder: -30,
  },
  {
    systemDefaultKey: "accounts.handled_os_onboarding_strain",
    name: "Onboarding Strain",
    filters: accountFilters(scoreView("cf:Onboarding_Strain_Score__c", "cf:Onboarding_Strain_Detail__c")),
    sortOrder: -20,
  },
  // Accounts that received any Handled OS signal, most recent first
  // (last_signal_at = derived_fields.last_signal_at, the latest signal timestamp).
  // Reuses the existing recent_signals system-view key so re-seeding updates it
  // in place rather than orphaning it.
  {
    systemDefaultKey: "accounts.recent_signals",
    name: "Recent Signals",
    filters: accountFilters({
      receivedSignals: [...HANDLED_OS_SIGNAL_TYPES],
      sortColumn: "last_signal_at",
      sortDirection: "desc",
      visibleColumns: ["name", "owner", "receivedSignals", "lastInteraction"],
    }),
    sortOrder: -10,
  },
];

export const HANDLED_OS_OPPORTUNITY_VIEWS: HandledOsDefaultView[] = [
  {
    systemDefaultKey: "opportunities.handled_os_open",
    name: "Open Opportunities",
    filters: opportunityFilters({ isClosed: false }),
    sortOrder: -20,
  },
  {
    systemDefaultKey: "opportunities.handled_os_closing_this_month",
    name: "Closing This Month",
    filters: opportunityFilters({ isClosed: false, closeDateBucket: "thisMonth" }),
    sortOrder: -10,
  },
];

// Fallback used when a user has no saved default: the "My Accounts" view.
export const HANDLED_OS_ACCOUNTS_FALLBACK_FILTERS: SavedFilterViewFilters = {
  ...HANDLED_OS_ACCOUNT_VIEWS[0].filters,
};

export const HANDLED_OS_OPPORTUNITIES_FALLBACK_FILTERS: SavedFilterViewFilters = {
  ...HANDLED_OS_OPPORTUNITY_VIEWS[0].filters,
  visibleColumns: [...HANDLED_OS_OPPORTUNITY_VISIBLE_COLUMNS],
};
